import { DEFAULT_TIMEOUT_MS, MAX_DESCRIPTION_LENGTH, MAX_TIMEOUT_MS, MIN_TIMEOUT_MS, SUPPORTED_CONTRACT_VERSIONS } from '../constants'
import { type ExternalDataSource, type ExternalDataSourceUpdateBody, type StoredApiKey } from '../../../models/ExternalDataSource'

export interface DataSourceFormValues {
  name: string
  description: string
  callbackUrl: string
  dataTypes: string[]
  contractVersion: string
  timeoutMs: string
  useApiKey: boolean
  headerName: string
  apiKey: string
}

export const EMPTY_DATA_SOURCE_FORM: DataSourceFormValues = {
  name: '',
  description: '',
  callbackUrl: '',
  dataTypes: [],
  contractVersion: SUPPORTED_CONTRACT_VERSIONS[0],
  timeoutMs: String(DEFAULT_TIMEOUT_MS),
  useApiKey: false,
  headerName: 'X-Api-Key',
  apiKey: '',
}

export const isHttpsUrl = (value: string) => URL.canParse(value) && new URL(value).protocol === 'https:'

export const parseTimeout = (values: DataSourceFormValues) => Number(values.timeoutMs)

export const isTimeoutValid = (values: DataSourceFormValues) => {
  const parsed = parseTimeout(values)
  return Number.isInteger(parsed) && parsed >= MIN_TIMEOUT_MS && parsed <= MAX_TIMEOUT_MS
}

export const isAuthValid = (values: DataSourceFormValues, storedApiKey: StoredApiKey = null) => {
  if (!values.useApiKey) {
    return true
  }
  if (values.headerName.trim() === '') {
    return false
  }
  if (values.apiKey.trim() !== '') {
    return true
  }
  return storedApiKey !== null && storedApiKey.headerName === values.headerName.trim()
}

export const isDescriptionValid = (values: DataSourceFormValues) => values.description.trim().length <= MAX_DESCRIPTION_LENGTH

export const isDataSourceFormValid = (values: DataSourceFormValues, storedApiKey: StoredApiKey = null) =>
  values.name.trim() !== '' &&
  isDescriptionValid(values) &&
  isHttpsUrl(values.callbackUrl.trim()) &&
  values.dataTypes.length > 0 &&
  isTimeoutValid(values) &&
  isAuthValid(values, storedApiKey)

const sameDataTypes = (left: string[], right: string[]) => left.length === right.length && left.every((value, index) => value === right[index])

export const buildUpdateBody = (dataSource: ExternalDataSource, values: DataSourceFormValues): ExternalDataSourceUpdateBody => {
  const body: ExternalDataSourceUpdateBody = {}
  if (values.name.trim() !== dataSource.name) {
    body.name = values.name.trim()
  }
  if (values.description.trim() !== (dataSource.description ?? '')) {
    body.description = values.description.trim() === '' ? null : values.description.trim()
  }
  if (values.callbackUrl.trim() !== dataSource.callbackUrl) {
    body.callbackUrl = values.callbackUrl.trim()
  }
  if (!sameDataTypes(values.dataTypes, dataSource.dataTypes)) {
    body.dataTypes = values.dataTypes
  }
  if (values.contractVersion !== dataSource.contractVersion) {
    body.contractVersion = values.contractVersion
  }
  if (parseTimeout(values) !== dataSource.fetchPolicy.timeoutMs) {
    body.fetchPolicy = { timeoutMs: parseTimeout(values) }
  }
  if (!values.useApiKey && dataSource.auth !== null) {
    body.auth = null
  }
  if (values.useApiKey && values.apiKey.trim() !== '') {
    body.auth = { type: 'apiKey', headerName: values.headerName.trim(), apiKey: values.apiKey.trim() }
  }
  return body
}
